/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: App.js
 *
 *  author: Thomas CLERET (thomas.cleret@proton.me)
 *
 *  creation: 16/9/2025 - 16:24
 *
 *  description: This file contains the top-level components of the E2RH-View
 * front-end software application
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

// Global imports
import * as React from "react";
import PropTypes from "prop-types";

// Project imports
import Login from "./menus/login.tsx";
import * as utils from "./utils/utils.tsx";
import NavBar from "./menus/navbar.tsx";
import OpAgrume from "./actions/opAgrume.tsx";
import Dashboard from "./actions/dashboard.tsx";
import SearchEmployee from "./actions/searchEmployee.tsx";
import Migration from "./actions/migration.tsx";
import ImportCsv from "./actions/importCsv.tsx";
import Robopenpaye from "./actions/robopenpaye.tsx";
import SetPassword from "./actions/setPassword.tsx";
import AdminPage from "./actions/adminPage.tsx";

const isTest = false;
/**
 * Main view of the page, handles view logic:
 * - connexion
 * - navbar
 * - different components display
 *
 * The page is compose of either the login screen, or the content of the site.
 * The content is accessed through the navbar, or the account menu.
 */
export default function E2RH_View() {
   let actionsListStartValue = [];
   let accountActionsListStartValue = [];
   if (isTest) {
      actionsListStartValue = [
         "Dashboard",
         "OpAgrume",
         "MigrSilaeOp/SearchEmp",
         "MigrSilaeOp/Migrate",
         "MigrSilaeOp/ImportCsv",
         "Robopenpaye",
      ];
      accountActionsListStartValue = [
         utils.AccountAction.AdminPage,
         utils.AccountAction.SetPasswd,
         utils.AccountAction.Logout,
      ];
   }
   const [actionsList, setActionsList] = React.useState(actionsListStartValue);
   const [accountActionsList, setAccountActionsList] = React.useState(accountActionsListStartValue);
   const [isLoggedIn, setIsLoggedIn] = React.useState(false);
   const [domainsList, setDomainsList] = React.useState([]);
   const [currentAction, setCurrentAction] = React.useState(utils.MenuAction.Dashboard);

   // Display the login screen or the app screen depending on the login state
   if (!isLoggedIn && !isTest) {
      return (
         <Login
            onSetActionsList={setActionsList}
            onSetAccountActionsList={setAccountActionsList}
            onSetCurrentAction={setCurrentAction}
            onSetIsLoggedIn={setIsLoggedIn}
            onSetDomainsList={setDomainsList}
         />
      );
   } else {
      return (
         <div>
            <NavBar
               actions={actionsList}
               curr_action={currentAction}
               accountActions={accountActionsList}
               onSetCurrentAction={setCurrentAction}
               onSetIsLoggedIn={setIsLoggedIn}
            />
            <div className="action_wrapper">
               <DisplayTool action={currentAction} domainsList={domainsList} onSetCurrentAction={setCurrentAction} />
            </div>
         </div>
      );
   }
}

/**
 * Calls the UI object that corresponds to the given action
 *
 * @param {str} action The action to display
 * @param {list} domainsList The domains list for the current user
 * @param {callback} onSetCurrentAction The callback for the action modifyer
 * @returns the corresponding JSX object
 */
function DisplayTool({ action, domainsList, onSetCurrentAction }) {
   const [data, setData] = React.useState(utils.MenuAction.None);

   // Create callbacks to set the App state
   const setCurrentAction = React.useCallback(
      (event) => {
         onSetCurrentAction(event);
      },
      [onSetCurrentAction]
   );

   // If action changed, write in the state to trigger the redraw of the component
   if (action != data) {
      setData(action);
      if (action !== utils.AccountAction.Logout) {
         localStorage.setItem("last_action", action);
      }
   }

   // Return the correct object
   if (action === utils.MenuAction.OpAgrume) {
      return <OpAgrume domainsList={domainsList} />;
   } else if (action === utils.MenuAction.MigrSilaeOp + "/" + utils.MigrationAction.SearchEmp) {
      return <SearchEmployee domainsList={domainsList} />;
   } else if (action === utils.MenuAction.MigrSilaeOp + "/" + utils.MigrationAction.Migrate) {
      return <Migration />;
   } else if (action === utils.MenuAction.MigrSilaeOp + "/" + utils.MigrationAction.ImportCsv) {
      return <ImportCsv />;
   } else if (action === utils.MenuAction.Robopenpaye) {
      return <Robopenpaye domainsList={domainsList} />;
   } else if (action === utils.AccountAction.SetPasswd) {
      return <SetPassword onSetCurrentAction={setCurrentAction} />;
   } else if (action === utils.AccountAction.AdminPage) {
      return <AdminPage />;
   } else {
      return <Dashboard />;
   }
}

DisplayTool.propTypes = {
   action: PropTypes.string,
   domainsList: PropTypes.arrayOf(PropTypes.string),
   onSetCurrentAction: PropTypes.func,
};
